/**
 * @component ry-heading
 * @category  display
 * @summary   Section heading with optional subtitle (Fomantic UI `.ui.header` equivalent). The element's own innerHTML becomes the title; a `sub` attribute adds a smaller descriptive line beneath. Pure display — no events, no methods.
 *
 * Reference: Fomantic UI's header element. ry-heading covers the common
 * page-section / panel-header case: a heading text plus optional subheading.
 * For inline `h1`–`h6` semantics, use the native tags inside the heading
 * slot or instead of `<ry-heading>` entirely.
 *
 * @attribute sub      string?              Optional subheading text rendered in a smaller, muted slot beneath the title.
 * @attribute size     'sm' | 'lg'?         Size variant. Default size matches the body theme — `sm` shrinks the heading, `lg` enlarges it.
 * @attribute align    'center' | 'right'?  Text alignment. Default is `start` (typically left). `center` and `right` are pure-CSS hooks.
 * @attribute divider  boolean              Renders a divider rule below the entire heading group (title + sub). Useful for visually separating sections.
 *
 * @content   Title text or HTML (e.g. nested `<span>`, `<strong>`, or an icon). The original innerHTML is lifted into `[data-ry-target="title"]` at setup. After setup, do NOT read `el.innerHTML` to find the title text — query the title slot.
 *
 * @dom
 *   ry-heading[size?][align?][divider?]
 *     > div.ry-heading__title    [data-ry-target="title"]
 *         > {original innerHTML}
 *     > div.ry-heading__sub      [data-ry-target="sub"]    (only when [sub] attribute is non-empty)
 *         > {sub text}
 *
 * @gotcha  The original innerHTML is consumed and replaced. To read or update the rendered title after mount, target `[data-ry-target="title"]`, not `el.textContent` or `el.innerHTML`.
 *
 * @gotcha  Setting `sub=""` (empty string) does NOT render an empty sub slot — the slot is omitted entirely. Use a non-empty string or omit the attribute.
 *
 * @gotcha  Updating attributes (size/align/divider) post-mount is honored by CSS (they are styling hooks), but updating `sub` after mount does NOT re-render the sub slot. The transform runs once at setup. Set the attribute before mount, or replace the element.
 *
 * @gotcha  This is NOT an `<h1>`/`<h2>`/etc. — `<ry-heading>` does not provide a semantic heading level. If document outline matters, embed a native heading tag in the slot: `<ry-heading><h2>Section</h2></ry-heading>`.
 *
 * @example basic
 * ```html
 * <ry-heading>Page Title</ry-heading>
 * ```
 *
 * @example with-sub
 * ```html
 * <ry-heading sub="A brief description">Section Title</ry-heading>
 * ```
 *
 * @example centered
 * ```html
 * <ry-heading align="center" sub="Subtitle below">Centered Heading</ry-heading>
 * ```
 *
 * @example right-aligned
 * ```html
 * <ry-heading align="right">Right-aligned title</ry-heading>
 * ```
 *
 * @example size-small
 * ```html
 * <ry-heading size="sm">Small heading</ry-heading>
 * ```
 *
 * @example size-large
 * ```html
 * <ry-heading size="lg" sub="Marketing copy">Big Hero Heading</ry-heading>
 * ```
 *
 * @example with-divider
 * ```html
 * <ry-heading divider sub="Optional subheading">Section Header</ry-heading>
 * <p>Body content follows after the divider rule.</p>
 * ```
 *
 * @example semantic-h2
 * ```html
 * <ry-heading><h2>Real h2 inside the slot</h2></ry-heading>
 * ```
 *
 * @tutorial mount-lifts-content-into-title
 * ```js
 * const el = mount('<ry-heading>Welcome</ry-heading>');
 * const title = el.querySelector('[data-ry-target="title"]');
 * await expectEqual(title.textContent.trim(), 'Welcome', 'title slot holds the original text');
 * ```
 *
 * @tutorial sub-attribute-renders-sub-slot
 * ```js
 * const el = mount('<ry-heading sub="A description">Title</ry-heading>');
 * const sub = el.querySelector('[data-ry-target="sub"]');
 * await expectEqual(!!sub, true, 'sub slot exists');
 * await expectEqual(sub.textContent.trim(), 'A description');
 * ```
 *
 * @tutorial no-sub-slot-without-sub-attribute
 * ```js
 * // Wrong assumption: the sub slot always renders, possibly empty.
 * // It does NOT — omit the [sub] attribute and the slot is absent.
 * const el = mount('<ry-heading>Title only</ry-heading>');
 * await expectEqual(el.querySelector('[data-ry-target="sub"]'), null, 'no sub slot when attribute absent');
 * ```
 *
 * @tutorial empty-sub-attribute-renders-no-slot
 * ```js
 * // Wrong assumption: sub="" creates an empty visible slot. It does NOT —
 * // empty string is treated as no subtitle.
 * const el = mount('<ry-heading sub="">Title</ry-heading>');
 * await expectEqual(el.querySelector('[data-ry-target="sub"]'), null, 'empty sub attribute renders no slot');
 * ```
 *
 * @tutorial preserves-nested-markup
 * ```js
 * const el = mount('<ry-heading><strong>Bold</strong> heading</ry-heading>');
 * const title = el.querySelector('[data-ry-target="title"]');
 * await expectEqual(!!title.querySelector('strong'), true, 'nested elements preserved');
 * ```
 *
 * @tutorial attribute-hooks-are-css-only
 * ```js
 * // size/align/divider are pure CSS hooks. The JS does nothing with them
 * // beyond forwarding the attribute to the host element so CSS can read it.
 * const el = mount('<ry-heading size="lg" align="center" divider>x</ry-heading>');
 * await expectAttr(el, 'size', 'lg');
 * await expectAttr(el, 'align', 'center');
 * await expectEqual(el.hasAttribute('divider'), true);
 * ```
 *
 * @see ry-text
 * @see ry-card
 */

import { RyElement } from '../core/ry-element.js';

export class RyHeading extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="title"]')) return;

    const subText = this.getAttribute('sub') ?? '';
    const titleText = this.innerHTML.trim();

    this.innerHTML = `
      <div data-ry-target="title" class="ry-heading__title">${titleText}</div>
      ${subText ? `<div data-ry-target="sub" class="ry-heading__sub">${subText}</div>` : ''}
    `;
  }
}

customElements.define('ry-heading', RyHeading);
